import { useState } from "react";
import { format } from "date-fns";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, CreditCard, Loader2, AlertTriangle, Lock } from "lucide-react";
import { getPlanCta } from "@/lib/planCta";

const BENEFITS = [
  "Unlimited properties",
  "Professional evidence bundle PDFs",
  "Public photo verification links",
  "Witness & maintenance share links",
  "Deposit deduction auditor",
  "Move-out prep plan",
  "AI-drafted return-request & dispute letters",
];

const fmtDate = (d) => { try { return format(new Date(d), "MMM d, yyyy"); } catch { return ""; } };

export function BillingSection() {
  const { tier, status, currentPeriodEnd, trialEligible, trialing, trialEnd, openUpgrade } = useTier();
  const [loading, setLoading] = useState(false);
  const isPremium = tier === "premium";
  const cta = getPlanCta({ trialEligible, trialing });

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/create-portal-session", { origin: window.location.origin });
      window.location.href = data.portal_url;
    } catch {
      setLoading(false);
      toast.error("Couldn't open the billing portal. Please try again.");
    }
  };

  return (
    <div className="w-full rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="billing-section">
      <h2 className="font-heading text-lg font-semibold text-navy flex items-center gap-2">
        <CreditCard size={18} className="text-brand" /> Plan &amp; billing
      </h2>

      {status === "past_due" && (
        <div data-testid="billing-pastdue-banner" className="mt-4 flex items-start gap-2 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Payment issue — update your card to keep TenantSeal Plus.{" "}
            <button onClick={openPortal} className="font-semibold underline">Manage billing</button>
          </span>
        </div>
      )}

      {isPremium ? (
        <div className="mt-4" data-testid="billing-premium">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-soft border border-amber-border px-2.5 py-0.5 text-xs font-semibold text-amber-ink">
              <Sparkles size={12} /> Plus
            </span>
            {trialing ? (
              <span className="text-sm text-[#475569]" data-testid="billing-trialing-state">
                trial ends {trialEnd ? fmtDate(trialEnd) : "soon"}
              </span>
            ) : currentPeriodEnd ? (
              <span className="text-sm text-[#475569]">renews {fmtDate(currentPeriodEnd)}</span>
            ) : null}
          </div>
          <Button data-testid="manage-subscription-btn" onClick={openPortal} disabled={loading}
            variant="outline" className="mt-4 rounded-xl">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CreditCard size={16} className="mr-2" />}
            Manage subscription
          </Button>
        </div>
      ) : (
        <div className="mt-4" data-testid="billing-free">
          <p className="text-sm text-[#475569]">Current plan: <span className="font-semibold text-navy">Free</span></p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">TenantSeal Plus includes:</p>
          <ul className="mt-2 space-y-2 text-sm text-[#64748B]">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex items-center gap-2"><Lock size={14} className="text-[#94A3B8] shrink-0" /> {b}</li>
            ))}
          </ul>
          <div className="mt-4 rounded-xl bg-[#F1F5F9] px-4 py-3 text-center">
            {trialEligible ? (
              <p className="font-heading text-base font-bold text-navy">{cta.price} <span className="font-normal text-[#64748B]">{cta.priceSub}</span></p>
            ) : (
              <><span className="font-heading text-xl font-bold text-navy">$6</span><span className="text-sm text-[#64748B]">/month — cancel anytime</span></>
            )}
          </div>
          <Button data-testid="upgrade-to-premium-btn" onClick={() => openUpgrade("settings")}
            className="mt-4 w-full h-11 rounded-xl bg-navy hover:bg-navy-hover text-white font-semibold">
            <Sparkles size={16} className="mr-2" /> {cta.cta}
          </Button>
        </div>
      )}
    </div>
  );
}
